import {
  useGetDemoQuery,
  useLazyGetModificationsQuery,
} from '@carboncopyco/data';
import {
  TourCapturePage,
  clearTourCreatorState,
  hasInitialTourCreatorState,
  selectIsShowingDimensionPanel,
  setInitialTourCreatorState,
  useTourSelector,
} from '@carboncopyco/mimesis-sdk';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import type { Dimension } from '@carboncopyco/types';
import type { IGlobalTab } from '@carboncopyco/types/desktop';
import { settings } from '../../../settings';
import { ConfirmExitAlert } from '../components/EditorContainer';
import InnerViewArea from '../components/InnerViewArea';
import { WorkspaceSelectionField } from '../components/Workspaces/WorkspaceSelectionField';
import { useCustomNavigate } from '../hooks/useCustomNavigate';
import { useFocusAppService } from '../hooks/useFocusAppService';
import { useFocusModalAppService } from '../hooks/useFocusModalAppService';
import useInnerViewReadyForZoom from '../hooks/useInnerViewReadyForZoom';
import { useNavigation } from '../hooks/useNavigation';
import rendererService from '../services/RendererService';
import { useTabManager } from '../services/TabManager';
import { isActiveOrAllInnerTabsLoaded } from '../shared/utils';
import debuggerInterceptorPathListState from '../shared/utils/debugger-interceptor-path-list-state';
import { useTabs, useUser } from '../store/context';
import { useResponsiveModeContext } from '../store/context/ResponsiveModeContext';

export const TourCaptureRoute = ({ tab }: { tab: IGlobalTab }) => {
  const dispatch = useDispatch();
  const { canGoBack, canGoForward, goBack, goForward } = useNavigation(tab);
  const {
    updateGlobalTabPayload,
    closeGlobalTab,
    getActiveGlobalTab,
    toggleInnerViewsVisibility,
  } = useTabManager();
  const globalTab = getActiveGlobalTab();
  const { isOpenCloseTabDialog, setIsOpenCloseTabDialog } = useTabs();
  const { triggerFocus } = useFocusAppService();
  const { updateImageReplacePathListStateModification } =
    debuggerInterceptorPathListState(tab);
  const webViewRef = useRef(null);
  const [modalClosed, setModalClosed] = useState(true);
  const { activeOrganization } = useUser();
  const [isDraftLoadedOnce, setIsDraftLoadedOnce] = useState(false);
  const workspaceId = globalTab.workspaceId;
  const { data: demo } = useGetDemoQuery(
    {
      workspaceId: workspaceId || '',
      organizationId: activeOrganization?.id?.toString(),
      id: tab.demoId || '',
    },
    { skip: !tab.demoId || !workspaceId }
  );

  const uuid = String(tab.innerTabs[0].id);
  const isStateInitialised = useTourSelector(hasInitialTourCreatorState(uuid));
  const isInnerViewLoaded = isActiveOrAllInnerTabsLoaded(tab);

  const { isReady: isInnerViewReadyForZoom } = useInnerViewReadyForZoom();

  const isShowingDimensionPanel = useTourSelector(
    selectIsShowingDimensionPanel(uuid)
  );
  const activeTabId = globalTab?.id;
  const innerTabs = globalTab?.innerTabs;
  const innerTabsIds = innerTabs.map((innerTab) => innerTab.id);
  const restartTour = () => {
    dispatch(clearTourCreatorState({ uuid }));
    setModalClosed(false);
    window.dsDesktop.navigation.reload();
    window.dsDesktop.tour.restartCapturing(activeTabId);
  };

  const isTabActive = tab.id === activeTabId;

  /**
   *  When we are opening TourCaptureRoute we need to hide other inner tabs.
   *  And when we are leaving this tab, we need to show other inner tabs.
   *  This is fix for dimensions panel
   */
  useEffect(() => {
    if (isTabActive) {
      toggleInnerViewsVisibility({
        activeTabId,
        show: false,
      });
    }

    return () => {
      if (isTabActive) {
        toggleInnerViewsVisibility({
          activeTabId,
          show: true,
        });
      }
    };
  }, [isTabActive]);

  const [fetchModifications, { data: modifications }] =
    useLazyGetModificationsQuery();
  const getModifications = () =>
    fetchModifications({
      organizationId: activeOrganization?.id?.toString(),
      entityType: 'demo',
      entityId: String(tab.demoId ?? ''),
      apiVersion: 2,
    }).unwrap();

  useEffect(() => {
    if (!demo?.id) {
      return;
    }

    // eslint-disable-next-line no-console
    getModifications().catch(console.error);
  }, [demo?.id]);

  useEffect(() => {
    if (demo && modifications && !isDraftLoadedOnce) {
      updateImageReplacePathListStateModification(modifications);
      const updatedPayload = {
        ...tab.payload,
        modifications,
        draftPlayerToken: demo.playerToken,
      };
      updateGlobalTabPayload(tab.id, updatedPayload);
      rendererService.createGlobalTab({
        ...tab,
        payload: updatedPayload,
      });
      setIsDraftLoadedOnce(true);
    }
  }, [demo, modifications]);

  const {
    setDeviceSize,
    setIsResponsiveModeActive,
    setInnerViewZoomFactor,
    recalculateZoomFactor,
  } = useResponsiveModeContext();

  useEffect(() => {
    if (!isStateInitialised) {
      dispatch(setInitialTourCreatorState({ uuid }));
    }
  }, [isStateInitialised]);

  useEffect(() => {
    triggerFocus(isShowingDimensionPanel);
  }, [isShowingDimensionPanel]);

  useEffect(() => {
    return () => {
      dispatch(clearTourCreatorState({ uuid }));
      innerTabsIds.forEach((tabId) => {
        setInnerViewZoomFactor?.(1, tabId);
      });
    };
  }, []);

  useEffect(() => {
    if (modalClosed) {
      triggerFocus(false);
    }
  }, [modalClosed]);

  useEffect(() => {
    if (isInnerViewLoaded && !modalClosed) {
      triggerFocus(true);
    }
  }, [isInnerViewLoaded]);

  useEffect(() => {
    if (activeTabId === tab.id && !modalClosed) {
      setTimeout(() => {
        triggerFocus(false);
      }, 0);
    }
  }, [activeTabId, tab, modalClosed]);

  useEffect(() => {
    if (!isInnerViewReadyForZoom || !tab.activeInnerTabId) return;

    recalculateZoomFactor?.(tab.activeInnerTabId);
  }, [isInnerViewReadyForZoom]);

  const handleDimensionsChange = useCallback(
    (dimension: Dimension, type: string, fitFactor: number) => {
      innerTabsIds.forEach((innerTabId) => {
        setIsResponsiveModeActive(type !== 'responsive', innerTabId, dimension);

        setInnerViewZoomFactor?.(
          type === 'responsive' ? 1 : fitFactor,
          innerTabId
        );
      });

      if (type === 'responsive') {
        setDeviceSize({
          width: null,
          height: null,
        });
        return;
      }

      if (dimension.width) {
        setDeviceSize({
          width: dimension.width,
          height: dimension.height,
        });
      }
    },
    []
  );

  return (
    <div
      className={`flex flex-col flex-grow shrink basis-0 min-h-0 ${
        isInnerViewLoaded ? '' : 'bg-white'
      }`}
    >
      {isStateInitialised && (
        <TourCapturePage
          useNavigate={useCustomNavigate}
          dynamicRecordingUrl={`${settings.RECORDER_URL}/register`}
          fileServiceDynamicLink={settings.FILES_SERVICE_URL}
          WorkspaceSelectionField={WorkspaceSelectionField}
          uuid={uuid}
          onDimensionChange={handleDimensionsChange}
          webViewRef={webViewRef}
          webViewFrame={
            <div
              ref={webViewRef}
              className="flex flex-col min-w-0 flex-1 h-full relative "
            >
              <InnerViewArea tab={tab} />
            </div>
          }
          onModalClosed={() => setModalClosed(true)}
          desktopHooks={{
            useFocusModalAppService,
            useTabManager,
          }}
          tab={tab}
          activeTabId={activeTabId}
          desktopNavigate={{
            canGoBack,
            canGoForward,
            goBack,
            goForward,
          }}
          onStartCapturing={() => {
            updateGlobalTabPayload(activeTabId, {
              tourRecordingInProgress: true,
            });
          }}
          onRestartCapturing={restartTour}
          onModificationsReady={updateImageReplacePathListStateModification}
        />
      )}
      <ConfirmExitAlert
        title="Leave the tour"
        description="Discard your progress and leave the tour?"
        yesLabel="Discard and exit"
        isOpen={isOpenCloseTabDialog}
        onClose={() => setIsOpenCloseTabDialog(false)}
        onExit={() => {
          closeGlobalTab(activeTabId);
        }}
      />
    </div>
  );
};
